import React, { ReactNode } from 'react';
import App from 'next/app';
import NextAppRouter from 'next/router';
import { BrowserMetrics, WebVital } from '@spotify-internal/semantic-metrics';
import { KeyboardDetectionProvider } from '@spotify-internal/encore-web';
import { RemoteConfigProvider } from '@spotify-internal/remote-config-integration-react';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { captureException } from '@sentry/nextjs';
import 'style-imports';

import { Header } from 'components/Header/Header';
import { Footer } from 'components/Footer/Footer';
import { sendMetric, withRouteMetrics } from 'utils/metrics';
import analytics from 'utils/client/analytics/analytics';
import MessagingProviderWrapper from 'providers/messaging/MessagingProviderWrapper';
import { TranslationProvider } from 'providers/translations/TranslationProvider';
import { SharedPageData } from 'utils/shared-page-data';
import UbiLoggerProvider from 'utils/ubi/ubiProvider';
import { Layout, MainContainer } from 'components/Layout';
import { getVerticalConfig } from 'utils/vertical';
import MastheadProvider from 'providers/MastheadProvider';
import S4AMastheadProviderWrapper from 'providers/S4AMastheadProviderWrapper';
import { MastheadData } from 'lib/sanity/types';
import GlobalStyle from 'components/GlobalStyle';
import ServerSideConstantsProvider from 'providers/ServerSideConstantsProvider';

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  const ReactDOM = require('react-dom');
  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

export class CustomApp extends App<{
  pageProps: { sharedPageData: SharedPageData; mastheadData?: MastheadData; Component: ReactNode };
}> {
  componentDidCatch(error: Error) {
    captureException(error);
  }

  async componentDidMount() {
    const { pageProps } = this.props;
    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) return;

    const { market, usePreview } = pageProps.sharedPageData;

    // Preview page should not show cookie bar. As a consequence, no tracking for these visits.
    if (!usePreview) {
      analytics.initialize(market.marketCode);
    }
  }

  render() {
    const { Component, pageProps } = this.props;

    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) {
      return <Component {...pageProps} />;
    }

    const { remoteConfigJSON, market, translations, isPlain, isLoggedIn, vertical } =
      pageProps.sharedPageData as SharedPageData;

    const verticalConfig = getVerticalConfig(vertical);

    const configuration = remoteConfigJSON ? Configuration.fromJSON(remoteConfigJSON) : null;
    return (
      // Constants with primitive values can be bundled into this provider to avoid boilerplate
      <ServerSideConstantsProvider isPlain={isPlain} market={market} verticalConfig={verticalConfig}>
        <Layout theme={verticalConfig.theme}>
          <GlobalStyle />
          <UbiLoggerProvider isLoggedIn={isLoggedIn}>
            <RemoteConfigProvider configuration={configuration}>
              <KeyboardDetectionProvider>
                <TranslationProvider translations={translations}>
                  <MessagingProviderWrapper>
                    <MastheadProvider turnkeyMastheadData={pageProps.mastheadData}>
                      <S4AMastheadProviderWrapper>
                        {!isPlain && <Header />}
                        <MainContainer>
                          <Component {...pageProps} />
                        </MainContainer>
                        {!isPlain && <Footer />}
                      </S4AMastheadProviderWrapper>
                    </MastheadProvider>
                  </MessagingProviderWrapper>
                </TranslationProvider>
              </KeyboardDetectionProvider>
            </RemoteConfigProvider>
          </UbiLoggerProvider>
        </Layout>
      </ServerSideConstantsProvider>
    );
  }
}

export function reportWebVitals(metric: WebVital) {
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route: NextAppRouter.router?.route ?? 'unknown',
    }),
  );
}

export default withRouteMetrics(CustomApp as any);
